/**
 * ReplanningDashboard — R148o · Feb 15 2026
 *
 * Approver-facing dashboard for the Task Reschedule Approval Workflow.
 * Anyone can navigate here (route is workspace-scoped) but only holders
 * of `task.reschedule.approve` see the Approve/Reject actions — others
 * see a read-only list of pending requests.
 *
 * Route: /app/replanning
 * Backend:
 *   GET  /workspaces/{ws}/reschedule-requests?status=pending
 *   POST /reschedule-requests/{rid}/approve
 *   POST /reschedule-requests/{rid}/reject
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { apiErrorMessage } from "@/lib/apiError";
import { toast } from "sonner";
import { fmtRelative, fmtDate } from "@/lib/format";
import { CalendarClock, CheckCircle2, XCircle, User, Clock } from "lucide-react";

const STATUS_TABS = [
  { k: "pending",   label: "Pending" },
  { k: "approved",  label: "Approved" },
  { k: "rejected",  label: "Rejected" },
  { k: "cancelled", label: "Cancelled" },
  { k: "all",       label: "All" },
];

export default function ReplanningDashboard() {
  const { activeWorkspaceId } = useAuth();
  const [tab, setTab] = useState("pending");
  const [rows, setRows] = useState([]);
  const [canApprove, setCanApprove] = useState(false);
  const [busy, setBusy] = useState(true);
  const [noteDrafts, setNoteDrafts] = useState({});
  // R150 · Bulk approve/reject for end-of-week batches. Only pending rows
  // are selectable; the bulk bar only shows when at least one is picked.
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [bulkNote, setBulkNote] = useState("");

  const load = async () => {
    if (!activeWorkspaceId) return;
    setBusy(true);
    try {
      const { data } = await api.get(
        `/workspaces/${activeWorkspaceId}/reschedule-requests`,
        { params: { status: tab } },
      );
      setRows(data?.rows || []);
      setCanApprove(!!data?.can_approve);
      setSelected(new Set());
    } catch (e) { toast.error(apiErrorMessage(e, "Failed to load requests")); }
    finally { setBusy(false); }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [activeWorkspaceId, tab]);

  const decide = async (rid, verb) => {
    try {
      await api.post(`/reschedule-requests/${rid}/${verb}`, {
        decision_note: (noteDrafts[rid] || "").trim() || null,
      });
      toast.success(verb === "approve" ? "Approved — dates applied" : "Rejected");
      setNoteDrafts((d) => { const c = { ...d }; delete c[rid]; return c; });
      load();
    } catch (e) { toast.error(apiErrorMessage(e, `${verb} failed`)); }
  };

  const toggleRow = (rid) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(rid)) next.delete(rid); else next.add(rid);
      return next;
    });
  };
  const pendingIds = rows.filter((r) => r.status === "pending").map((r) => r.id);
  const allPendingSelected = pendingIds.length > 0 && pendingIds.every((id) => selected.has(id));
  const toggleAll = () => {
    if (allPendingSelected) setSelected(new Set());
    else setSelected(new Set(pendingIds));
  };
  const bulkDecide = async (verb) => {
    const ids = Array.from(selected).filter((id) => pendingIds.includes(id));
    if (ids.length === 0) return;
    const label = verb === "approve" ? "Approve" : "Reject";
    // eslint-disable-next-line no-alert
    if (!window.confirm(`${label} ${ids.length} request${ids.length === 1 ? "" : "s"}?`)) return;
    setBulkBusy(true);
    const note = bulkNote.trim() || null;
    let ok = 0; let failed = 0;
    for (const rid of ids) {
      try {
        await api.post(`/reschedule-requests/${rid}/${verb}`, { decision_note: note });
        ok += 1;
      } catch { failed += 1; }
    }
    setBulkBusy(false);
    setBulkNote("");
    if (ok > 0) toast.success(`${label}d ${ok}${failed ? ` · ${failed} failed` : ""}`);
    else toast.error("All decisions failed");
    load();
  };

  return (
    <div className="min-h-screen">
      <PageHeader
        eyebrow="PLANNING · APPROVALS"
        title="Replanning Dashboard"
        description="Pending task reschedule requests. Approvers apply the new dates in one click; others see it read-only. Notification fires to the requester on decision."
      />
      <div className="px-4 sm:px-6 md:px-10 py-6 space-y-4">
        {/* Tabs */}
        <div className="border border-border bg-muted/40 p-1 inline-flex" data-testid="replanning-tabs">
          {STATUS_TABS.map((t) => (
            <button
              key={t.k}
              type="button"
              onClick={() => setTab(t.k)}
              className={`px-3 py-1 text-[11px] uppercase tracking-widest ${tab === t.k ? "bg-primary text-primary-foreground" : "hover:bg-muted/60"}`}
              data-testid={`replanning-tab-${t.k}`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {busy && (
          <div className="border border-border p-8 text-center text-sm text-muted-foreground" data-testid="replanning-loading">
            Loading requests…
          </div>
        )}

        {!busy && canApprove && pendingIds.length > 0 && (
          <div className="border border-border bg-muted/30 px-3 py-2 flex flex-wrap items-center gap-3" data-testid="replanning-bulk-bar">
            <label className="inline-flex items-center gap-2 text-xs font-medium cursor-pointer">
              <input
                type="checkbox"
                checked={allPendingSelected}
                onChange={toggleAll}
                data-testid="replanning-bulk-select-all"
              />
              Select all pending ({pendingIds.length})
            </label>
            <span className="text-[11px] text-muted-foreground">
              {selected.size} selected
            </span>
            <input
              value={bulkNote}
              onChange={(e) => setBulkNote(e.target.value)}
              placeholder="Batch decision note (optional)"
              className="text-xs px-2 py-1 border border-border bg-background flex-1 min-w-[200px]"
              data-testid="replanning-bulk-note"
            />
            <Button
              variant="outline"
              className="rounded-none h-8 text-xs"
              disabled={selected.size === 0 || bulkBusy}
              onClick={() => bulkDecide("reject")}
              data-testid="replanning-bulk-reject"
            >
              <XCircle className="w-3.5 h-3.5 mr-1" /> Reject {selected.size || ""}
            </Button>
            <Button
              className="rounded-none h-8 text-xs"
              disabled={selected.size === 0 || bulkBusy}
              onClick={() => bulkDecide("approve")}
              data-testid="replanning-bulk-approve"
            >
              <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve {selected.size || ""}
            </Button>
          </div>
        )}

        {!busy && rows.length === 0 && (
          <div className="border border-border p-10 text-center" data-testid="replanning-empty">
            <CalendarClock className="w-8 h-8 text-muted-foreground/50 mx-auto mb-2" />
            <div className="text-sm font-semibold">Nothing {tab === "all" ? "here yet" : `${tab}`}</div>
            <p className="text-xs text-muted-foreground mt-1 max-w-md mx-auto">
              When a teammate without <b>task.reschedule.approve</b> tries to move a task&apos;s planned start or due date,
              their change lands here for you to approve.
            </p>
          </div>
        )}

        <div className="space-y-3" data-testid="replanning-list">
          {rows.map((r) => {
            const changes = r.changes || {};
            const done = r.status !== "pending";
            return (
              <div
                key={r.id}
                className={`border border-border bg-background ${done ? "opacity-70" : ""}`}
                data-testid={`replanning-row-${r.id}`}
              >
                <div className="px-4 py-3 border-b border-border bg-muted/40 flex items-start gap-3 flex-wrap">
                  {canApprove && r.status === "pending" && (
                    <input
                      type="checkbox"
                      checked={selected.has(r.id)}
                      onChange={() => toggleRow(r.id)}
                      className="mt-1 shrink-0"
                      data-testid={`replanning-select-${r.id}`}
                      aria-label="Select for bulk action"
                    />
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground flex items-center gap-1">
                      <User className="w-3 h-3" /> {r.requested_by_name || "—"}
                      <span className="mx-1">·</span>
                      <Clock className="w-3 h-3" /> {fmtRelative(r.created_at)}
                      {r.status !== "pending" && (
                        <span className="ml-2 chip text-[10px]" data-testid={`replanning-status-${r.id}`}>
                          {r.status}
                        </span>
                      )}
                    </div>
                    <Link to={`/app/tasks?open=${r.task_id}`} className="text-base font-semibold hover:underline truncate block" data-testid={`replanning-task-link-${r.id}`}>
                      {r.task_title || r.task_id}
                    </Link>
                  </div>
                </div>
                <div className="p-4 grid gap-4 md:grid-cols-2">
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Proposed date changes
                    </div>
                    <ul className="text-xs space-y-1" data-testid={`replanning-changes-${r.id}`}>
                      {Object.keys(changes).map((k) => (
                        <li key={k} className="grid grid-cols-[8rem_1fr_1fr] gap-2 items-center">
                          <span className="text-muted-foreground uppercase tracking-widest text-[10px]">{k.replace(/_/g, " ")}</span>
                          <span className="text-rose-700 line-through">{fmtDate(changes[k].from) || "—"}</span>
                          <span className="text-emerald-700 font-semibold">{fmtDate(changes[k].to) || "—"}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-1">
                      Justification
                    </div>
                    <div className="text-xs whitespace-pre-wrap border-l-2 border-primary/40 pl-2 text-foreground/85">
                      {r.reason || <em className="text-muted-foreground">No reason provided</em>}
                    </div>
                    {r.decision_note && (
                      <div className="text-[11px] mt-3 border-l-2 border-emerald-500 pl-2 whitespace-pre-wrap">
                        <b>{r.status === "approved" ? "Approved" : "Rejected"} by {r.decided_by_name || "—"}:</b>{" "}
                        {r.decision_note}
                      </div>
                    )}
                  </div>
                </div>
                {r.status === "pending" && canApprove && (
                  <div className="px-4 pb-4 space-y-2 border-t border-border pt-3">
                    <Textarea
                      rows={2}
                      value={noteDrafts[r.id] || ""}
                      onChange={(e) => setNoteDrafts((d) => ({ ...d, [r.id]: e.target.value }))}
                      placeholder="Decision note (optional — visible to the requester)"
                      className="rounded-none text-xs"
                      data-testid={`replanning-note-${r.id}`}
                    />
                    <div className="flex justify-end gap-2">
                      <Button
                        variant="outline"
                        className="rounded-none h-8 text-xs"
                        onClick={() => decide(r.id, "reject")}
                        data-testid={`replanning-reject-${r.id}`}
                      >
                        <XCircle className="w-3.5 h-3.5 mr-1" /> Reject
                      </Button>
                      <Button
                        className="rounded-none h-8 text-xs"
                        onClick={() => decide(r.id, "approve")}
                        data-testid={`replanning-approve-${r.id}`}
                      >
                        <CheckCircle2 className="w-3.5 h-3.5 mr-1" /> Approve &amp; apply
                      </Button>
                    </div>
                  </div>
                )}
                {r.status === "pending" && !canApprove && (
                  <div className="px-4 pb-3 text-[10px] text-muted-foreground italic">
                    Read-only — you don&rsquo;t hold task.reschedule.approve.
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
